import {
  ChevronDown,
  ChevronRight,
  FilePlus2,
  Folder,
  FolderPlus,
  Pencil,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { Button } from "../../Button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  ContextMenu,
  ContextMenuItems,
  ContextMenuVariant,
} from "../../ui/context-menu";
import { cn } from "../../utils";
import { FileIcon } from "../FileIcon";

import { FileTreeGuides, getTreeContentPaddingLeft } from "./Guides";
import { FileTreeInlineEditor } from "./InlineEditor";
import { FileTreeLabel } from "./Label";
import type { FileTreeNode, PendingFileTreeAction } from "./types";

export interface FileTreeItemProps {
  node: FileTreeNode;
  modifiedFilePaths?: ReadonlySet<string>;
  depth: number;
  isLast: boolean;
  ancestorHasNextSiblings: boolean[];
  selectedPath: string | null;
  activeDirectoryPath: string | null;
  onSelect: (path: string) => void;
  onSelectDirectory?: (path: string | null) => void;
  expandedFolders: Set<string>;
  onToggleFolder: (path: string) => void;
  onMoveNode?: (
    sourcePath: string,
    sourceType: "file" | "folder",
    targetFolderPath: string | null,
  ) => void;
  draggedItem?: {
    path: string;
    type: "file" | "folder";
  } | null;
  onDragStateChange?: (
    draggedItem: {
      path: string;
      type: "file" | "folder";
    } | null,
  ) => void;
  onDeleteNode?: (path: string, type: "file" | "folder") => void;
  onCreateFile?: (parentPath: string | null) => void;
  onCreateFolder?: (parentPath: string | null) => void;
  onRequestRename?: (path: string, type: "file" | "folder") => void;
  pendingAction?: PendingFileTreeAction | null;
  onPendingActionValueChange?: (value: string) => void;
  onSubmitPendingAction?: () => void;
  onCancelPendingAction?: () => void;
  readOnly?: boolean;
  isNodeProtected?: (path: string) => boolean;
  renderFileIcon?: (path: string, className?: string) => ReactNode;
}

const folderContainsModified = (
  folderPath: string,
  modifiedFilePaths: ReadonlySet<string> | undefined,
): boolean => {
  if (!modifiedFilePaths || modifiedFilePaths.size === 0) {
    return false;
  }
  const prefix = `${folderPath}/`;
  for (const filePath of modifiedFilePaths) {
    if (filePath.startsWith(prefix)) {
      return true;
    }
  }
  return false;
};

const isDropValid = (
  sourcePath: string,
  sourceType: "file" | "folder",
  targetFolderPath: string,
): boolean => {
  if (sourcePath === targetFolderPath) {
    return false;
  }
  if (
    sourceType === "folder" &&
    targetFolderPath.startsWith(`${sourcePath}/`)
  ) {
    return false;
  }
  return true;
};

const wrapWithContextMenu = (
  content: ReactNode,
  node: FileTreeNode,
  items: ContextMenuItems<FileTreeNode>[],
  readOnly: boolean,
) => {
  if (readOnly || items.length === 0) {
    return content;
  }

  return (
    <ContextMenu
      contextMenuItems={items}
      data={node}
      modal={false}
      className="block"
      preventAutoFocusOnClose
    >
      {content}
    </ContextMenu>
  );
};

export const FileTreeItem = ({
  node,
  modifiedFilePaths,
  depth,
  isLast,
  ancestorHasNextSiblings,
  selectedPath,
  activeDirectoryPath,
  onSelect,
  onSelectDirectory,
  expandedFolders,
  onToggleFolder,
  onMoveNode,
  draggedItem,
  onDragStateChange,
  onDeleteNode,
  onCreateFile,
  onCreateFolder,
  onRequestRename,
  pendingAction,
  onPendingActionValueChange,
  onSubmitPendingAction,
  onCancelPendingAction,
  readOnly = false,
  isNodeProtected,
  renderFileIcon,
}: FileTreeItemProps) => {
  const isProtected = isNodeProtected?.(node.path) ?? false;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOverValid, setDragOverValid] = useState(true);
  const fileInlinePaddingLeft = getTreeContentPaddingLeft(depth, "file");
  const folderInlinePaddingLeft = getTreeContentPaddingLeft(depth, "folder");
  const rowPaddingRightClass = "pr-2.5";
  const fileTreeGuides = (
    <FileTreeGuides
      path={node.path}
      depth={depth}
      isLast={isLast}
      ancestorHasNextSiblings={ancestorHasNextSiblings}
      contentPaddingLeft={fileInlinePaddingLeft}
    />
  );
  const folderTreeGuides = (
    <FileTreeGuides
      path={node.path}
      depth={depth}
      isLast={isLast}
      ancestorHasNextSiblings={ancestorHasNextSiblings}
      contentPaddingLeft={folderInlinePaddingLeft}
    />
  );
  const isFolderModified = useMemo(
    () =>
      node.type === "folder" &&
      folderContainsModified(node.path, modifiedFilePaths),
    [node.type, node.path, modifiedFilePaths],
  );

  const handleDragEnter = useCallback(() => {
    if (readOnly || !onMoveNode || node.type !== "folder") {
      return;
    }
    const sourcePath = draggedItem?.path;
    const sourceType = draggedItem?.type;
    if (!sourcePath || !sourceType) {
      return;
    }
    const valid = isDropValid(sourcePath, sourceType, node.path);
    setDragOverValid(valid);
    setIsDragOver(true);
  }, [draggedItem, node, onMoveNode, readOnly]);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    const related = event.relatedTarget as Node | null;
    if (related && (event.currentTarget as Node).contains(related)) {
      return;
    }
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (readOnly || !onMoveNode || node.type !== "folder") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      const sourcePath = draggedItem?.path;
      const sourceType = draggedItem?.type;
      if (!sourcePath || !sourceType) {
        return;
      }
      const valid = isDropValid(sourcePath, sourceType, node.path);
      setDragOverValid(valid);
      setIsDragOver(true);
    },
    [draggedItem, node, onMoveNode, readOnly],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (readOnly || !onMoveNode || node.type !== "folder") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      const sourcePath = event.dataTransfer.getData("text/skill-path");
      const sourceType = event.dataTransfer.getData("text/skill-type") as
        | "file"
        | "folder";
      if (!sourcePath || !sourceType) {
        return;
      }
      if (!isDropValid(sourcePath, sourceType, node.path)) {
        return;
      }
      onDragStateChange?.(null);
      onMoveNode(sourcePath, sourceType, node.path);
    },
    [node, onDragStateChange, onMoveNode, readOnly],
  );

  if (node.type === "file") {
    const treeLabel = node.name;
    const isSelected = selectedPath === node.path;
    const isModified = modifiedFilePaths?.has(node.path) ?? false;
    const isRenaming =
      pendingAction?.mode === "rename" && pendingAction.path === node.path;
    const parentPath = node.path.includes("/")
      ? node.path.split("/").slice(0, -1).join("/")
      : null;

    if (
      isRenaming &&
      onPendingActionValueChange &&
      onSubmitPendingAction &&
      onCancelPendingAction
    ) {
      return (
        <FileTreeInlineEditor
          value={pendingAction.value}
          error={pendingAction.error}
          placeholder="file-name.md"
          paddingLeft={fileInlinePaddingLeft}
          treeGuides={fileTreeGuides}
          leadingContent={
            renderFileIcon?.(node.path) ?? <FileIcon path={node.path} />
          }
          onChange={onPendingActionValueChange}
          onSubmit={onSubmitPendingAction}
          onCancel={onCancelPendingAction}
        />
      );
    }

    const rowContent = (
      <div
        className={cn(
          "group/treeItem relative flex h-8 cursor-pointer select-none items-center rounded-lg text-sm",
          rowPaddingRightClass,
          isSelected
            ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100"
            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
        )}
        style={{ paddingLeft: `${fileInlinePaddingLeft}px` }}
        onClick={() => onSelect(node.path)}
        onDragStart={
          readOnly || isProtected || !onMoveNode
            ? undefined
            : (event) => {
                event.dataTransfer.setData("text/skill-path", node.path);
                event.dataTransfer.setData("text/skill-type", "file");
                event.dataTransfer.effectAllowed = "move";
                onDragStateChange?.({ path: node.path, type: "file" });
              }
        }
        onDragEnd={
          readOnly || isProtected || !onMoveNode
            ? undefined
            : () => onDragStateChange?.(null)
        }
        draggable={!readOnly && !isProtected && !!onMoveNode}
        role="treeitem"
      >
        {fileTreeGuides}
        <div className="relative flex min-w-0 flex-1 items-center gap-1.5">
          {renderFileIcon?.(node.path, cn(isSelected ? "text-blue-500" : "text-gray-400")) ?? (
            <FileIcon
              path={node.path}
              className={cn(isSelected ? "text-blue-500" : "text-gray-400")}
            />
          )}
          <FileTreeLabel
            label={treeLabel}
            path={node.path}
            readOnly={readOnly}
          />
          {isModified && (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500"
              aria-label="Modified file"
              title="Modified"
            />
          )}
        </div>
        {!readOnly && !isProtected && onDeleteNode && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 shrink-0 rounded-md p-0 opacity-0 transition-opacity group-hover/treeItem:opacity-100"
            onClick={(event) => {
              event.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            title="Delete file"
          >
            <Trash2 className="h-3.5 w-3.5 text-red-500" />
          </Button>
        )}
      </div>
    );

    if (readOnly) {
      return rowContent;
    }

    const contextMenuItems: ContextMenuItems<FileTreeNode>[] = [
      {
        id: "new-file",
        label: "New File",
        icon: FilePlus2,
        onClick: () => onCreateFile?.(parentPath),
      },
      {
        id: "new-folder",
        label: "New Folder",
        icon: FolderPlus,
        onClick: () => onCreateFolder?.(parentPath),
      },
      ...(isProtected
        ? []
        : [
            { id: "divider-top", isDivider: true } as const,
            {
              id: "rename",
              label: "Rename",
              icon: Pencil,
              onClick: () => onRequestRename?.(node.path, "file"),
            } as const,
            { id: "divider-bottom", isDivider: true } as const,
            {
              id: "delete",
              label: "Delete",
              icon: Trash2,
              variant: ContextMenuVariant.DESTRUCTIVE,
              onClick: () => setDeleteDialogOpen(true),
            } as const,
          ]),
    ];

    return (
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        {wrapWithContextMenu(rowContent, node, contextMenuItems, readOnly)}
        <AlertDialogContent onClick={(event) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{node.name}&quot;? This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => onDeleteNode?.(node.path, "file")}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  const folderTreeLabel = node.name;
  const isExpanded = expandedFolders.has(node.path);
  const isSelectedDirectory = activeDirectoryPath === node.path;
  const isRenaming =
    pendingAction?.mode === "rename" && pendingAction.path === node.path;
  const isCreatingWithinFolder =
    pendingAction?.mode === "create" && pendingAction.parentPath === node.path;
  const childNodes = node.children ?? [];
  const showNestedTree = childNodes.length > 0 || isCreatingWithinFolder;
  const childAncestorHasNextSiblings = [...ancestorHasNextSiblings, !isLast];
  const showFolderActions =
    !readOnly && (onCreateFile || onCreateFolder) && isSelectedDirectory;

  const folderRowContent = (
    <div
      className={cn(
        "group/treeItem relative flex h-8 cursor-pointer select-none items-center rounded-lg text-sm",
        rowPaddingRightClass,
        isSelectedDirectory
          ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100"
          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
        isDragOver &&
          (dragOverValid
            ? "bg-blue-50/80 ring-2 ring-inset ring-blue-400"
            : "bg-red-50/60 ring-2 ring-inset ring-red-300"),
      )}
      onClick={() => onToggleFolder(node.path)}
      onDragStart={
        readOnly || isProtected || !onMoveNode
          ? undefined
          : (event) => {
              event.dataTransfer.setData("text/skill-path", node.path);
              event.dataTransfer.setData("text/skill-type", "folder");
              event.dataTransfer.effectAllowed = "move";
              onDragStateChange?.({ path: node.path, type: "folder" });
            }
      }
      onDragEnter={readOnly || !onMoveNode ? undefined : handleDragEnter}
      onDragOver={readOnly || !onMoveNode ? undefined : handleDragOver}
      onDragLeave={readOnly || !onMoveNode ? undefined : handleDragLeave}
      onDrop={readOnly || !onMoveNode ? undefined : handleDrop}
      onDragEnd={
        readOnly || isProtected || !onMoveNode
          ? undefined
          : () => onDragStateChange?.(null)
      }
      draggable={!readOnly && !isProtected && !!onMoveNode && !isRenaming}
      role="treeitem"
      style={{ paddingLeft: `${folderInlinePaddingLeft}px` }}
    >
      {folderTreeGuides}
      <div className="relative flex min-w-0 flex-1 items-center gap-1.5">
        {isExpanded ? (
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              isSelectedDirectory || (isDragOver && dragOverValid)
                ? "text-blue-500"
                : "text-gray-500",
            )}
          />
        ) : (
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              isSelectedDirectory || (isDragOver && dragOverValid)
                ? "text-blue-500"
                : "text-gray-500",
            )}
          />
        )}
        <Folder
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            isSelectedDirectory || (isDragOver && dragOverValid)
              ? "text-blue-500"
              : "text-gray-500",
          )}
        />
        <FileTreeLabel
          label={folderTreeLabel}
          path={node.path}
          readOnly={readOnly}
        />
        {isFolderModified && (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500"
            aria-label="Contains modified files"
            title="Contains modified files"
          />
        )}
      </div>
      {!readOnly && (showFolderActions || onDeleteNode) && (
        <div
          className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover/treeItem:opacity-100"
          onClick={(event) => event.stopPropagation()}
        >
          {onCreateFolder && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 rounded-md p-0"
              onClick={() => onCreateFolder(node.path)}
              title="New folder"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {onCreateFile && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 rounded-md p-0"
              onClick={() => onCreateFile(node.path)}
              title="New file"
            >
              <FilePlus2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isProtected && onDeleteNode && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 rounded-md p-0"
              onClick={() => setDeleteDialogOpen(true)}
              title="Delete folder"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          )}
        </div>
      )}
    </div>
  );

  if (readOnly) {
    return (
      <div className="relative">
        {folderRowContent}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-in-out",
            isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            {showNestedTree && (
              <div>
                <div className="relative">
                  {childNodes.map((child, index) => (
                    <FileTreeItem
                      key={child.path}
                      node={child}
                      modifiedFilePaths={modifiedFilePaths}
                      depth={depth + 1}
                      isLast={
                        index === childNodes.length - 1 &&
                        !isCreatingWithinFolder
                      }
                      ancestorHasNextSiblings={childAncestorHasNextSiblings}
                      selectedPath={selectedPath}
                      activeDirectoryPath={activeDirectoryPath}
                      onSelect={onSelect}
                      onSelectDirectory={onSelectDirectory}
                      expandedFolders={expandedFolders}
                      onToggleFolder={onToggleFolder}
                      onMoveNode={onMoveNode}
                      draggedItem={draggedItem}
                      onDragStateChange={onDragStateChange}
                      onDeleteNode={onDeleteNode}
                      onCreateFile={onCreateFile}
                      onCreateFolder={onCreateFolder}
                      onRequestRename={onRequestRename}
                      pendingAction={pendingAction}
                      onPendingActionValueChange={onPendingActionValueChange}
                      onSubmitPendingAction={onSubmitPendingAction}
                      onCancelPendingAction={onCancelPendingAction}
                      readOnly={readOnly}
                      isNodeProtected={isNodeProtected}
                      renderFileIcon={renderFileIcon}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const folderContextMenuItems: ContextMenuItems<FileTreeNode>[] = [
    {
      id: "new-file",
      label: "New File",
      icon: FilePlus2,
      onClick: () => onCreateFile?.(node.path),
    },
    {
      id: "new-folder",
      label: "New Folder",
      icon: FolderPlus,
      onClick: () => onCreateFolder?.(node.path),
    },
    ...(isProtected
      ? []
      : [
          { id: "divider-top", isDivider: true } as const,
          {
            id: "rename",
            label: "Rename",
            icon: Pencil,
            onClick: () => onRequestRename?.(node.path, "folder"),
          } as const,
          { id: "divider-bottom", isDivider: true } as const,
          {
            id: "delete",
            label: "Delete",
            icon: Trash2,
            variant: ContextMenuVariant.DESTRUCTIVE,
            onClick: () => setDeleteDialogOpen(true),
          } as const,
        ]),
  ];

  return (
    <div className="relative">
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        {isRenaming &&
        onPendingActionValueChange &&
        onSubmitPendingAction &&
        onCancelPendingAction ? (
          <FileTreeInlineEditor
            value={pendingAction.value}
            error={pendingAction.error}
            placeholder="folder-name"
            paddingLeft={folderInlinePaddingLeft}
            treeGuides={folderTreeGuides}
            leadingContent={
              <>
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                )}
                <Folder className="h-3.5 w-3.5 shrink-0 text-gray-500" />
              </>
            }
            onChange={onPendingActionValueChange}
            onSubmit={onSubmitPendingAction}
            onCancel={onCancelPendingAction}
          />
        ) : (
          wrapWithContextMenu(
            folderRowContent,
            node,
            folderContextMenuItems,
            readOnly,
          )
        )}
        <AlertDialogContent onClick={(event) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{node.name}&quot; and all
              its contents? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => onDeleteNode?.(node.path, "folder")}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          {showNestedTree && (
            <div>
              <div className="relative">
                {childNodes.map((child, index) => (
                  <FileTreeItem
                    key={child.path}
                    node={child}
                    modifiedFilePaths={modifiedFilePaths}
                    depth={depth + 1}
                    isLast={
                      index === childNodes.length - 1 && !isCreatingWithinFolder
                    }
                    ancestorHasNextSiblings={childAncestorHasNextSiblings}
                    selectedPath={selectedPath}
                    activeDirectoryPath={activeDirectoryPath}
                    onSelect={onSelect}
                    onSelectDirectory={onSelectDirectory}
                    expandedFolders={expandedFolders}
                    onToggleFolder={onToggleFolder}
                    onMoveNode={onMoveNode}
                    draggedItem={draggedItem}
                    onDragStateChange={onDragStateChange}
                    onDeleteNode={onDeleteNode}
                    onCreateFile={onCreateFile}
                    onCreateFolder={onCreateFolder}
                    onRequestRename={onRequestRename}
                    pendingAction={pendingAction}
                    onPendingActionValueChange={onPendingActionValueChange}
                    onSubmitPendingAction={onSubmitPendingAction}
                    onCancelPendingAction={onCancelPendingAction}
                    readOnly={readOnly}
                    isNodeProtected={isNodeProtected}
                    renderFileIcon={renderFileIcon}
                  />
                ))}
                {isCreatingWithinFolder &&
                  onPendingActionValueChange &&
                  onSubmitPendingAction &&
                  onCancelPendingAction && (
                    <FileTreeInlineEditor
                      value={pendingAction.value}
                      error={pendingAction.error}
                      placeholder={
                        pendingAction.itemType === "folder"
                          ? "folder-name"
                          : "file-name.md"
                      }
                      paddingLeft={
                        pendingAction.itemType === "folder"
                          ? getTreeContentPaddingLeft(depth + 1, "folder")
                          : getTreeContentPaddingLeft(depth + 1, "file")
                      }
                      treeGuides={
                        <FileTreeGuides
                          path={`${node.path}/__pending__${pendingAction.itemType}`}
                          depth={depth + 1}
                          isLast
                          ancestorHasNextSiblings={childAncestorHasNextSiblings}
                          contentPaddingLeft={getTreeContentPaddingLeft(
                            depth + 1,
                            pendingAction.itemType,
                          )}
                        />
                      }
                      leadingContent={
                        pendingAction.itemType === "folder" ? (
                          <div className="pl-0.5">
                            <Folder className="h-4 w-4 shrink-0 text-gray-500" />
                          </div>
                        ) : (
                          (renderFileIcon?.(pendingAction.value || "new-file.md") ?? (
                            <FileIcon path={pendingAction.value || "new-file.md"} />
                          ))
                        )
                      }
                      onChange={onPendingActionValueChange}
                      onSubmit={onSubmitPendingAction}
                      onCancel={onCancelPendingAction}
                    />
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
